import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
  isEmptyString,
  isNilOrZero,
} from '@populateui/utils';
import {
  CommuniqueRecipientDTO,
  FormCategoryDTO,
  FormWorkflowInitiationRequirementsDTO,
  FormViewDTO,
  PatientVisitDTO,
  PhoneNumberDTO,
  UserDTO,
} from 'dtos';
import {
  FormCategory,
} from 'dtos/formCategory';
import {
  FormWorkflowStatus,
} from 'dtos/formWorkflowStatus';
import Controller from 'core/components/FormController';
import AutoCompletedInput from 'core/components/AutoComplete';
import Select from 'core/components/Select';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Dialog from 'pages/Dashboard/components/Dialog';
import Input from 'pages/Dashboard/components/Input';
import PhoneInput from 'core/components/PhoneInput';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Skeleton from 'core/components/Skeleton';
import {
  useNotify,
} from 'core/hooks';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import {
  useGetHistoricalVisitNotesByPatientId,
  useGetPhoneNumberTag,
  useGetUpcomingAppointments,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  visitNoteFormEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  formatDate,
} from 'utils/date';
import {
  getDisplayName,
  normalizePhoneNumber,
  validateEmailIfSet,
  validatePatient,
  validatePhoneIfSet,
} from 'pages/Dashboard/utils/helper';
import {
  normalizePatientVisits,
} from 'pages/Dashboard/pages/Charts/pages/Details/utils/helper';
import {
  useFormAutoFocus,
} from 'views/EMR/hooks/common';
import {
  useGetFormWorkflowInitiationCategories,
} from 'views/EMR/Settings/PdfTemplateBuilder/hooks';
import {
  StartFormWorkflowDialogProps,
  StartFormWorkflowStep,
} from 'views/EMR/components/FormWorkflow/types';
import {
  useStartFormWorkflowRequirements,
  usePatientFormStatuses,
  useStartFormWorkflow,
} from 'views/EMR/components/FormWorkflow/hooks';
import FormWorkflowFieldLabel from 'views/EMR/components/FormWorkflow/FormWorkflowFieldLabel';
import StartFormWorkflowFormPicker from 'views/EMR/components/FormWorkflow/StartFormWorkflowFormPicker';
import {
  CategoryLabel,
  CategoryOption,
  mapFormCategoriesToOptions,
} from 'views/EMR/components/FormWorkflow/categoryOptions';
import {
  getFormWorkflowFromTransitionResponse,
  getFormWorkflowPatientMultiMediaIds,
  isOrderOrReferralCategory,
  resolveAssigneeFromVisit,
} from 'views/EMR/components/FormWorkflow/utils';
import Tag from 'core/components/Tag';
import noop from 'lodash/noop';
import Name from 'pages/Dashboard/components/Name';
import DropdownOptionRow from 'core/components/Lookup/DropdownOptionRow';
import {
  Button,
} from '@populateui/ui';

type WizardFormValues = {
  patient: PatientCompact | null;
  formCategory: FormCategory | null;
  selectedForms: FormViewDTO[];
  encounter: PatientVisitDTO | null;
  assignee: UserDTO | null;
  emailSelected: boolean;
  email: string;
  phoneSelected: boolean;
  phone: string;
};

const formWorkflowRoutingFieldTooltips = {
  patient: 'The patient completing or receiving these forms.',
  visit: 'Attaches the forms to a visit and auto-fills visit-related fields on the PDF.',
  assigneeClinician: 'The clinician completing the non-patient portion of the forms.',
  patientContact: 'Choose how to send forms to the patient',
};

const staticWizardFormDefaults: Pick<
  WizardFormValues,
  'formCategory' | 'selectedForms' | 'encounter' | 'emailSelected' | 'phoneSelected'
> = {
  formCategory: null,
  selectedForms: [],
  encounter: null,
  emailSelected: false,
  phoneSelected: false,
};

type Props = StartFormWorkflowDialogProps;

type VisitLabelProps = {
  visit: PatientVisitDTO;
};

type ClinicianLabelProps = {
  user: UserDTO;
};

function VisitLabel({ visit }: VisitLabelProps) {
  return (
    <div className="flex items-center gap-2">
      <p className="font-semibold">{formatDate(visit?.visitDateTime ?? '')}</p>
      <Tag className="max-w-[250px] truncate" tagName={visit?.typeOfVisitDescription ?? ''} />
      <span className="text-gray-500">•</span>
      <span className="text-gray-700 font-medium">{getDisplayName(visit?.provider?.formattedNames)}</span>
    </div>
  );
}

function ClinicianLabel({ user }: ClinicianLabelProps) {
  return (
    <Name
      {...(user?.formattedNames ?? {})}
      image={{ size: 'xs' }}
      className="!w-full font-medium"
    />
  );
}

function getPatientPhoneString(phoneNumber?: string | PhoneNumberDTO | null): string {
  if (isNil(phoneNumber)) {
    return '';
  }

  if (typeof phoneNumber === 'string') {
    return isEmptyString(phoneNumber) ? '' : phoneNumber;
  }

  return phoneNumber.phoneNumberWithAreaCode ?? '';
}

export default function StartFormWorkflowDialog({
  patient: defaultPatient,
  encounterId: defaultEncounterId,
  lockEncounter = false,
  categoryFilter,
  defaultCategory,
  onClose,
}: Props) {
  const notify = useNotify();
  const userInfo = useUserInfo();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<StartFormWorkflowStep>('selection');
  const [requirements, setRequirements] = useState<
    FormWorkflowInitiationRequirementsDTO | null
  >(null);

  const formProps = useForm<WizardFormValues>({
    mode: 'all',
    defaultValues: {
      ...staticWizardFormDefaults,
      formCategory: defaultCategory
        ?? (categoryFilter?.length === 1 ? categoryFilter[0] : null),
      patient: defaultPatient ?? null,
      assignee: userInfo?.user ?? null,
      email: defaultPatient?.email ?? '',
      phone: getPatientPhoneString(defaultPatient?.phoneNumber),
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isValid, isSubmitting },
  } = formProps;

  const [
    patient,
    formCategory,
    selectedForms,
    encounter,
    assignee,
    emailSelected,
    phoneSelected,
    email,
    phone,
  ] = watch([
    'patient',
    'formCategory',
    'selectedForms',
    'encounter',
    'assignee',
    'emailSelected',
    'phoneSelected',
    'email',
    'phone',
  ]);

  const {
    data: formCategories = [],
  } = useGetFormWorkflowInitiationCategories();

  const categoryOptions = useMemo(() => {
    const options = mapFormCategoriesToOptions(formCategories);

    if (isEmpty(categoryFilter)) {
      return options;
    }

    const allowedCategories = new Set(categoryFilter);
    return options.filter((option) => allowedCategories.has(option.value));
  }, [categoryFilter, formCategories]);

  const {
    data: formStatuses,
  } = usePatientFormStatuses(
    patient?.patientId,
    !isNil(patient?.patientId),
  );

  const formStatusByFormId = useMemo(
    () => new Map(
      (formStatuses?.forms ?? []).map((form) => [form.formId ?? 0, form]),
    ),
    [formStatuses?.forms],
  );

  const selectedFormIds = useMemo(
    () => selectedForms.map((form) => form.formId ?? 0).filter((formId) => formId > 0),
    [selectedForms],
  );

  const {
    data: historicalVisits = [],
    isLoading: isLoadingHistoricalVisits,
  } = useGetHistoricalVisitNotesByPatientId(patient?.patientId ?? 0);

  const {
    data: upcomingVisits = [],
    isLoading: isLoadingUpcomingVisits,
  } = useGetUpcomingAppointments(patient?.patientId ?? 0);

  const visitsLoading = isLoadingHistoricalVisits || isLoadingUpcomingVisits;

  const {
    isLoading: isLoadingPhoneNumberDetails,
    phoneTag,
  } = useGetPhoneNumberTag(phone);

  const normalizedVisits = useMemo(
    () => normalizePatientVisits([...historicalVisits, ...upcomingVisits], {}),
    [historicalVisits, upcomingVisits],
  );

  const { data: users } = useGetUsers({ onlyDoctors: false });
  const activeUsers = useMemo(
    () => users?.filter((user) => user?.isActive && !user?.isServiceAccount) ?? [],
    [users],
  );

  const {
    mutateAsync: fetchRequirements,
    isPending: isLoadingRequirements,
  } = useStartFormWorkflowRequirements();

  const {
    mutateAsync: initiateWorkflow,
    isPending: isInitiating,
  } = useStartFormWorkflow();

  const patientLookupInputRef = useFormAutoFocus({
    enabled: step === 'selection' && isNil(patient?.patientId),
  });

  const getVisitLabel = useCallback((visit: PatientVisitDTO) => (
    <VisitLabel visit={visit} />
  ), []);

  const getCategoryLabel = useCallback((option: CategoryOption) => (
    <CategoryLabel option={option} />
  ), []);

  const applyAssigneeForVisit = useCallback((visit: PatientVisitDTO | null) => {
    if (!isNil(visit)) {
      setValue(
        'assignee',
        resolveAssigneeFromVisit(visit, activeUsers, userInfo?.user ?? null),
      );
    }
  }, [activeUsers, setValue, userInfo?.user]);

  useEffect(() => {
    if (!isNilOrZero(defaultEncounterId) && !isEmpty(normalizedVisits)) {
      const matchedVisit = normalizedVisits.find(
        (visit) => visit.encounterId === defaultEncounterId,
      );

      if (!isNil(matchedVisit)) {
        setValue('encounter', matchedVisit);
        applyAssigneeForVisit(matchedVisit);
      }
    }
  }, [applyAssigneeForVisit, defaultEncounterId, normalizedVisits, setValue]);

  const hadActiveUsersRef = useRef(false);

  useEffect(() => {
    const hasActiveUsers = activeUsers.length > 0;
    const usersJustLoaded = hasActiveUsers && !hadActiveUsersRef.current;
    hadActiveUsersRef.current = hasActiveUsers;

    if (usersJustLoaded && !isNil(encounter?.encounterId)) {
      applyAssigneeForVisit(encounter);
    }
  }, [activeUsers.length, applyAssigneeForVisit, encounter]);

  const handleClose = useCallback(() => {
    reset();
    setRequirements(null);
    onClose();
  }, [onClose, reset]);

  const goToRoutingStep = useCallback(async () => {
    if (selectedFormIds.length > 0) {
      const nextRequirements = await fetchRequirements({
        formIds: selectedFormIds,
      });

      setRequirements(nextRequirements);
      setStep('routing');
    }
  }, [
    fetchRequirements,
    selectedFormIds,
  ]);

  const buildPatientDestination = useCallback((): CommuniqueRecipientDTO | undefined => {
    if (!requirements?.requiresPatientContact) {
      return undefined;
    }

    return {
      email: emailSelected ? email : null,
      sms: phoneSelected ? normalizePhoneNumber(phone) : null,
      phone: phoneSelected ? normalizePhoneNumber(phone) : null,
    };
  }, [email, emailSelected, phone, phoneSelected, requirements?.requiresPatientContact]);

  const onInitiate = useCallback(async (values: WizardFormValues) => {
    if (isNil(values.patient?.patientId) || isEmpty(selectedFormIds)) {
      return;
    }

    try {
      const response = await initiateWorkflow({
        patientId: values.patient.patientId,
        formIds: selectedFormIds,
        encounterId: values.encounter?.encounterId ?? defaultEncounterId ?? null,
        assignedClinicianUserId: requirements?.requiresClinician
          ? values.assignee?.userId ?? userInfo?.user?.userId
          : null,
        patientDestination: buildPatientDestination(),
      });

      const workflow = getFormWorkflowFromTransitionResponse(response);
      const completionCategory = requirements?.forms?.[0]?.category as FormCategoryDTO | undefined;

      if (
        workflow?.formWorkflowId
        && workflow.assignedClinicianUserId === userInfo?.user?.userId
        && isNil(workflow.toDoId)
      ) {
        visitNoteFormEventChannel.emit('onOpenClinicianFormStep', {
          formWorkflowId: workflow.formWorkflowId,
          category: completionCategory ?? { name: 'Form', value: 'ProcedureForm' },
          patient: values.patient,
          encounterId: workflow.encounterId
            ?? values.encounter?.encounterId
            ?? defaultEncounterId
            ?? null,
        });
        handleClose();
        return;
      }

      if (
        workflow?.status === FormWorkflowStatus.Completed
        && isOrderOrReferralCategory(completionCategory)
      ) {
        // Fallback for workflows that were already completed before preview opens.
        // Alias-only Order/Referral workflows stay AwaitingClinician and use FillableForm instead,
        // because, order/referral user need to review and include/exclude visit note pdf from the final document.
        const mediaIds = getFormWorkflowPatientMultiMediaIds(workflow);

        if (mediaIds.length > 0 && !isNil(completionCategory)) {
          visitNoteFormEventChannel.emit('onOpenVisitNoteFormPreview', {
            patient: values.patient,
            patientMultiMediaIds: mediaIds,
            includeVisitNote: false,
            category: completionCategory,
          });
          handleClose();
          return;
        }
      }

      notify({
        variant: 'success',
        message: response.successMessage ?? 'Form workflow completed successfully',
      });
      handleClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to initiate form workflow';
      notify({ message, variant: 'error' });
    }
  }, [
    buildPatientDestination,
    defaultEncounterId,
    handleClose,
    initiateWorkflow,
    notify,
    requirements?.forms,
    requirements?.requiresClinician,
    selectedFormIds,
    userInfo?.user?.userId,
  ]);

  const selectionNextDisabled = (
    isLoadingRequirements
    || isNil(patient?.patientId)
    || isNil(formCategory)
    || selectedForms.length === 0
  );

  const routingDisabled = (
    isInitiating
    || isSubmitting
    || isNil(patient?.patientId)
    || (requirements?.requiresEncounter
        && isNil(encounter?.encounterId)
        && isNil(defaultEncounterId))
    || (requirements?.requiresClinician
        && isNil(assignee?.userId))
    || (
      requirements?.requiresPatientContact
      && !emailSelected
      && !phoneSelected
    )
    || (
      requirements?.requiresPatientContact
      && emailSelected
      && !validateEmailIfSet(email)
    )
    || (
      requirements?.requiresPatientContact
      && phoneSelected
      && (isLoadingPhoneNumberDetails || !validatePhoneIfSet(phone))
    )
  );

  return (
    <Dialog
      open
      title="Forms"
      onClose={handleClose}
      className="!w-140 !min-h-[380px] !space-y-0"
      titleClassName="!pb-4"
      maxWidth="sm"
      dialogRef={dialogRef as RefObject<HTMLDivElement>}
      autoFocusOptions={{ enabled: false }}
    >
      <FormProvider {...formProps}>
        <div className="flex flex-col flex-1 gap-form-row populate-form justify-between">
          <div className="flex flex-col gap-form-row">
            {step === 'selection' && (
              <div className="flex flex-col gap-form-row">
                <Controller
                  control={control}
                  name="patient"
                  rules={{ required: 'Select patient', validate: validatePatient }}
                  render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                    <PatientSelect
                      {...field}
                      value={value as PatientCompact}
                      className="!mb-0"
                      label="Patient"
                      error={invalid}
                      id="form-workflow-patient"
                      required
                      readonly={isSubmitting}
                      placeholder="Enter patient name, phone or date of birth"
                      allowDefault
                      inputRef={patientLookupInputRef as RefObject<HTMLInputElement>}
                      containerRef={dialogRef as RefObject<HTMLElement>}
                      disabled={isSubmitting}
                      onChange={(nextPatient) => {
                        setValue('formCategory', null);
                        setValue('selectedForms', []);
                        setValue('encounter', null);
                        setValue('email', (nextPatient as PatientCompact)?.email ?? '');
                        setValue('phone', getPatientPhoneString((nextPatient as PatientCompact)?.phoneNumber));
                        setRequirements(null);
                        onChange(nextPatient as PatientCompact);
                      }}
                    />
                  )}
                />

                {!isNil(patient?.patientId) && (
                  <Controller
                    control={control}
                    name="formCategory"
                    rules={{ required: 'Select category' }}
                    render={({ field }) => (
                      <Wrapper
                        id="form-workflow-category"
                        label="Form Category"
                        labelClasses="font-semibold text-xs text-gray-500 tracking-wide"
                        className="w-full"
                      >
                        <Select<CategoryOption>
                          id="form-workflow-category"
                          className="w-full"
                          selectClassName="!w-full"
                          value={categoryOptions.find(
                            (option) => option.value === field.value,
                          ) ?? null}
                          options={categoryOptions}
                          placeholder="Select form category"
                          getValue={(option) => option?.value ?? ''}
                          getLabel={getCategoryLabel}
                          onChange={(option) => {
                            setValue('selectedForms', []);
                            setRequirements(null);
                            field.onChange((option as CategoryOption).value);
                          }}
                        />
                      </Wrapper>
                    )}
                  />
                )}

                {!isNil(formCategory) && !isNil(patient?.patientId) && (
                  <Controller
                    control={control}
                    name="selectedForms"
                    rules={{
                      validate: (forms) => (
                        (forms?.length ?? 0) > 0 ? true : 'Select at least one form'
                      ),
                    }}
                    render={({ field }) => (
                      <Wrapper
                        id="form-workflow-forms"
                        label="Forms"
                        labelClasses="font-semibold text-xs text-gray-500 tracking-wide"
                        className="w-full"
                      >
                        <StartFormWorkflowFormPicker
                          category={formCategory}
                          patientId={patient.patientId}
                          value={field.value}
                          onChange={field.onChange}
                          formStatusByFormId={formStatusByFormId}
                          showStatus
                        />
                      </Wrapper>
                    )}
                  />
                )}
              </div>
            )}

            {step === 'routing' && !isNil(patient) && (
              <div className="flex flex-col gap-form-row">
                <PatientSelect
                  id="form-workflow-patient-routing"
                  value={patient}
                  label={(
                    <FormWorkflowFieldLabel
                      label="Patient"
                      infoTooltip={formWorkflowRoutingFieldTooltips.patient}
                    />
                  )}
                  readonly
                  className="!mb-0"
                  placeholder=""
                  onChange={noop}
                />

                {requirements?.requiresEncounter && (
                  <Controller
                    control={control}
                    name="encounter"
                    rules={{ required: 'Select visit' }}
                    render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                      <Wrapper
                        id="form-workflow-encounter"
                        label={(
                          <FormWorkflowFieldLabel
                            label="Visit"
                            infoTooltip={formWorkflowRoutingFieldTooltips.visit}
                          />
                        )}
                        className="w-full"
                      >
                        {visitsLoading ? (
                          <Skeleton className="!h-6 !w-full" variant="rectangular" />
                        ) : (
                          <Select<PatientVisitDTO>
                            {...field}
                            id="form-workflow-encounter"
                            className="w-full"
                            selectClassName="!w-full"
                            error={invalid}
                            showErrorText={false}
                            value={value}
                            options={normalizedVisits}
                            disabled={lockEncounter || visitsLoading}
                            placeholder="Select visit"
                            noOptionsText="No visits found"
                            getValue={(visit) => visit?.encounterId ?? ''}
                            getLabel={getVisitLabel}
                            onChange={(visit) => {
                              const nextVisit = visit as PatientVisitDTO;
                              onChange(nextVisit);
                              applyAssigneeForVisit(nextVisit);
                            }}
                          />
                        )}
                      </Wrapper>
                    )}
                  />
                )}

                {requirements?.requiresClinician && (
                  <Controller
                    control={control}
                    name="assignee"
                    rules={{ required: 'Select assignee clinician' }}
                    render={({ field: { value, onChange } }) => (
                      <Wrapper
                        id="form-workflow-assignee"
                        label={(
                          <FormWorkflowFieldLabel
                            label="Clinician"
                            infoTooltip={formWorkflowRoutingFieldTooltips.assigneeClinician}
                          />
                        )}
                        className="w-full"
                      >
                        <AutoCompletedInput<UserDTO>
                          id="form-workflow-assignee"
                          className="w-full"
                          blurOnSelect
                          openOnFocus
                          options={activeUsers}
                          value={value}
                          classes={{
                            inputRoot: '!pr-7 !py-2 !flex-nowrap',
                          }}
                          dropdownClassName="!overflow-hidden p-2"
                          getLabel={(user) => getDisplayName(user.formattedNames)}
                          renderValue={(selected) => {
                            const user = Array.isArray(selected) ? selected[0] : selected;

                            return !isNil(user) ? (
                              <span className="block max-w-full truncate">
                                <ClinicianLabel user={user as UserDTO} />
                              </span>
                            ) : null;
                          }}
                          renderOption={(props, option) => (
                            <DropdownOptionRow
                              key={option?.userId}
                              id={option?.userId?.toString()}
                              props={props}
                            >
                              <ClinicianLabel user={option} />
                            </DropdownOptionRow>
                          )}
                          isOptionEqualToValue={(option, selected) => (
                            option.userId === (selected as UserDTO)?.userId
                          )}
                          onChange={(nextUser) => { onChange(nextUser as UserDTO); }}
                        />
                      </Wrapper>
                    )}
                  />
                )}

                {requirements?.requiresPatientContact && (
                  <div className="flex flex-col gap-form-row">
                    <FormWorkflowFieldLabel
                      label="Patient Contact"
                      infoTooltip={formWorkflowRoutingFieldTooltips.patientContact}
                    />
                    <div className="flex flex-row">
                      <Controller
                        control={control}
                        name="emailSelected"
                        render={({ field }) => (
                          <Checkbox
                            label="Email"
                            id="form-workflow-email-selected"
                            onChange={field.onChange}
                            checked={field.value}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="email"
                        rules={{ required: emailSelected, validate: validateEmailIfSet }}
                        render={({ field, fieldState: { invalid } }) => (
                          <Input
                            {...field}
                            error={invalid}
                            showErrorText={false}
                            id="form-workflow-email"
                            className="text-gray-900 w-full ml-4 text-xs"
                          />
                        )}
                      />
                    </div>
                    <div className="flex flex-row">
                      <Controller
                        control={control}
                        name="phoneSelected"
                        render={({ field }) => (
                          <Checkbox
                            label="Text"
                            id="form-workflow-phone-selected"
                            onChange={field.onChange}
                            checked={field.value}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="phone"
                        rules={{ required: phoneSelected, validate: validatePhoneIfSet }}
                        render={({ field }) => (
                          <Wrapper
                            id="form-workflow-phone"
                            className="w-full ml-6"
                          >
                            <PhoneInput
                              {...field}
                              isLoadingPhoneTag={isLoadingPhoneNumberDetails}
                              phoneTag={phoneTag}
                              id="form-workflow-phone"
                              name="form-workflow-phone"
                            />
                          </Wrapper>
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-row-reverse justify-between">
            <DisabledFormTooltip>
              {step === 'selection' && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={selectionNextDisabled}
                  onClick={goToRoutingStep}
                >
                  Next
                </Button>
              )}
              {step === 'routing' && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={routingDisabled || !isValid}
                  onClick={handleSubmit(onInitiate)}
                >
                  {isInitiating ? 'Starting...' : 'Start'}
                </Button>
              )}
            </DisabledFormTooltip>
            <Button
              variant="outlined"
              type="button"
              onClick={step === 'selection'
                ? handleClose
                : () => { setStep('selection'); }}
            >
              {step === 'selection' ? 'Close' : 'Back'}
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
